import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Plus, FileText, CheckCircle, Clock, DollarSign, Loader2, Eye, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ERPBOQ = () => {
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [boqs, setBOQs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, boqsRes, projectsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/erp/boq/stats`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/erp/boq/boqs`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/erp/projects`, { headers: getAuthHeader() })
      ]);
      setStats(statsRes.data);
      setBOQs(boqsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const approveBOQ = async (boqId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/erp/boq/boqs/${boqId}/approve`, {}, { headers: getAuthHeader() });
      fetchData();
    } catch (error) {
      alert('Failed to approve BOQ');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">BOQ (Bill of Quantities)</h1>
          <p className="text-gray-600 mt-1">Manage project BOQs and estimates</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-primary hover:bg-primary/90">
          <Plus className="w-4 h-4 mr-2" />New BOQ
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Total BOQs</p><p className="text-2xl font-bold text-gray-900">{stats?.total_boqs || 0}</p></div><FileText className="w-10 h-10 text-blue-500" /></div></CardContent></Card>
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Approved</p><p className="text-2xl font-bold text-green-600">{stats?.approved_boqs || 0}</p></div><CheckCircle className="w-10 h-10 text-green-500" /></div></CardContent></Card>
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Draft</p><p className="text-2xl font-bold text-orange-600">{stats?.draft_boqs || 0}</p></div><Clock className="w-10 h-10 text-orange-500" /></div></CardContent></Card>
        <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-600">Total Value</p><p className="text-2xl font-bold text-green-600">₹{((stats?.total_value || 0) / 10000000).toFixed(2)}Cr</p></div><DollarSign className="w-10 h-10 text-green-500" /></div></CardContent></Card>
      </div>

      <Card>
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">All BOQs</h2>
          <div className="space-y-3">
            {boqs.map(boq => (
              <div key={boq.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold text-gray-900">{boq.name}</h3>
                    <span className={`px-2 py-1 text-xs rounded ${boq.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                      {boq.status}
                    </span>
                    <span className="text-xs text-gray-500">v{boq.version}</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{boq.description || 'No description'}</p>
                  <div className="flex gap-4 mt-2 text-sm">
                    <span className="text-gray-600">Items: {boq.items?.length || 0}</span>
                    <span className="font-medium text-green-600">₹{(boq.total_amount / 100000).toFixed(2)}L</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  {boq.status === 'draft' && (
                    <Button size="sm" onClick={() => approveBOQ(boq.id)} className="bg-green-600 hover:bg-green-700">
                      <CheckCircle className="w-4 h-4 mr-1" />Approve
                    </Button>
                  )}
                  <Button size="sm" variant="outline"><Eye className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
            {boqs.length === 0 && (
              <div className="text-center py-8 text-gray-500">No BOQs created yet. Create your first BOQ!</div>
            )}
          </div>
        </div>
      </Card>

      {showCreateModal && (
        <CreateBOQModal
          projects={projects}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => { fetchData(); setShowCreateModal(false); }}
          getAuthHeader={getAuthHeader}
        />
      )}
    </div>
  );
};

const CreateBOQModal = ({ projects, onClose, onSuccess, getAuthHeader }) => {
  const [formData, setFormData] = useState({
    project_id: '',
    name: '',
    description: '',
    version: '1.0'
  });
  const [items, setItems] = useState([{ item_code: '', description: '', unit: 'sqft', quantity: 0, rate: 0, category: 'civil' }]);
  const [loading, setLoading] = useState(false);

  const addItem = () => {
    setItems([...items, { item_code: '', description: '', unit: 'sqft', quantity: 0, rate: 0, category: 'civil' }]);
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/erp/boq/boqs`,
        { ...formData, items: items.map(item => ({ ...item, quantity: parseFloat(item.quantity), rate: parseFloat(item.rate) })) },
        { headers: getAuthHeader() }
      );
      onSuccess();
    } catch (error) {
      alert('Failed to create BOQ');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-2xl font-bold">Create New BOQ</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Project *</label>
              <select required value={formData.project_id} onChange={e => setFormData({...formData, project_id: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                <option value="">Select Project</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">BOQ Name *</label>
              <input required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} rows={2} className="w-full px-3 py-2 border rounded-lg" />
          </div>

          <div className="border-t pt-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-bold">BOQ Items</h3>
              <Button type="button" size="sm" onClick={addItem}><Plus className="w-4 h-4 mr-1" />Add Item</Button>
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {items.map((item, idx) => (
                <div key={idx} className="grid grid-cols-6 gap-2 p-2 bg-gray-50 rounded">
                  <input placeholder="Code" value={item.item_code} onChange={e => updateItem(idx, 'item_code', e.target.value)} className="px-2 py-1 border rounded text-sm" />
                  <input placeholder="Description" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} className="px-2 py-1 border rounded text-sm" />
                  <select value={item.unit} onChange={e => updateItem(idx, 'unit', e.target.value)} className="px-2 py-1 border rounded text-sm">
                    <option value="sqft">sqft</option><option value="cft">cft</option><option value="nos">nos</option><option value="kg">kg</option><option value="ton">ton</option><option value="rm">rm</option>
                  </select>
                  <input type="number" placeholder="Qty" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} className="px-2 py-1 border rounded text-sm" />
                  <input type="number" placeholder="Rate" value={item.rate} onChange={e => updateItem(idx, 'rate', e.target.value)} className="px-2 py-1 border rounded text-sm" />
                  <Button type="button" size="sm" variant="outline" onClick={() => removeItem(idx)}><Trash2 className="w-3 h-3" /></Button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={loading}>Cancel</Button>
            <Button type="submit" className="flex-1 bg-primary" disabled={loading}>{loading ? 'Creating...' : 'Create BOQ'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ERPBOQ;